import { cloneElement, isValidElement } from 'react';
import { useTranslation } from 'react-i18next';

import { TBaseIcon } from '@/shared/assets/icons/base-icon/base-icon.types';
import { CircleIcon } from '@/shared/assets/icons/circle-icon';
import {
  billingFeatureIcon,
  billingFeatureUnitTranslationKey,
} from '@/shared/features/billing/hooks/use-billing-entities/use-billing-entities.constants';
import { TUseBillingEntitiesProps } from '@/shared/features/billing/hooks/use-billing-entities/use-billing-entities.types';
import {
  BillingFeatureEnum,
  BillingOperationEnum,
  BillingPeriodEnum,
  BillingTimeInterval,
  BillingUpfrontProvisionedUsageItemFragment,
} from '@/shared/types/types.generated';
import {
  capitalizeString,
  formatFileSize,
  formatNumber,
  formatTimeUnit,
} from '@/shared/utils/format';

export const useBillingEntities = ({
  entitlementFeatures,
}: TUseBillingEntitiesProps = {}) => {
  const { t } = useTranslation();

  const getTierLabel = (tier?: string) => {
    if (!tier) {
      return '';
    }
    return capitalizeString(tier);
  };

  const getVersionLabel = (version?: string) => {
    if (!version) {
      return '';
    }
    return `(v${Number(version)})`;
  };

  const getConcatenatedFeaturesLabel = (feature?: BillingFeatureEnum) => {
    if (!feature) {
      return '';
    }

    if (
      !entitlementFeatures ||
      !(feature in entitlementFeatures) ||
      !entitlementFeatures[feature]
    ) {
      return getFeatureLabel(feature);
    }

    return entitlementFeatures[feature]
      .map((feature) => getFeatureLabel(feature))
      .join(' / ');
  };

  const getFeatureLabel = (feature: BillingFeatureEnum) => {
    return t(`billing:billing_feature_enum.${feature.toLowerCase()}`);
  };

  const getFeatureIcon = (
    feature: BillingFeatureEnum | BillingFeatureEnum[],
    props?: TBaseIcon,
  ) => {
    const selectedFeature = Array.isArray(feature) ? feature[0] : feature;
    const icon = billingFeatureIcon[selectedFeature] || <CircleIcon />;

    if (!isValidElement(icon)) {
      console.error('Icon must be a valid React element');
      return null;
    }

    return cloneElement(icon, props);
  };

  const getFeatureUnit = (feature: BillingFeatureEnum) => {
    const key = billingFeatureUnitTranslationKey[feature] || 'items';
    return t(`units:${key}`);
  };

  const isFormattedFeatureUnit = (feature: BillingFeatureEnum) => {
    const key = billingFeatureUnitTranslationKey[feature] || 'items';
    return key.includes('byte') || key.includes('minute');
  };

  const getFeatureQuantitiesUnit = (feature: BillingFeatureEnum, count = 0) => {
    const key = billingFeatureUnitTranslationKey[feature] || 'items';

    if (key.includes('byte')) {
      return formatFileSize(count);
    }

    if (key.includes('minute')) {
      if (!count || count <= 0) {
        return `0 ${t('common:time_units_long.hours')}`;
      }

      const hours = Math.floor(count / 60);
      const minutes = count % 60;
      const unit = (long: boolean) => (long ? 'time_units_long' : 'time_units');
      const parts: string[] = [];

      if (hours > 0) {
        parts.push(`${hours} ${t(`common:${unit(minutes === 0)}.hours`)}`);
      }

      if (minutes > 0) {
        parts.push(`${minutes} ${t(`common:${unit(hours === 0)}.minutes`)}`);
      }

      return parts.join(' ');
    }

    return t(`units:quantity_${key}`, { count, quantity: formatNumber(count) });
  };

  const getOperationLabel = (operation: BillingOperationEnum) => {
    return t(`billing:billing_operation_enum.${operation.toLowerCase()}`);
  };

  const getPeriodLabel = (period?: BillingPeriodEnum) => {
    const _period = period || BillingPeriodEnum.Month;
    return t(`billing:billing_period_enum.${_period.toLowerCase()}`);
  };

  const getDurationLabel = (
    duration?: BillingTimeInterval,
    fixedLabelKey = 'billing:expiring_after_period',
  ) => {
    if (!duration) return null;

    const formatted = formatTimeUnit(duration);
    const isRecurring = duration.recurring;

    if (!formatted) return null;

    return t(isRecurring ? 'billing:recurring_every_period' : fixedLabelKey, {
      period: `${formatted.count} ${t(`billing:billing_period_fixed.${formatted.key}`, { count: formatted.count })}`,
    });
  };

  const renderPeriodLabel = (period?: BillingPeriodEnum) => {
    let periodLabel = '';
    if (
      period &&
      ![BillingPeriodEnum.Tenancy, BillingPeriodEnum.Plan].includes(period)
    ) {
      periodLabel = ` ${t('billing:per_period', {
        period: getPeriodLabel(period),
      })}`;
    }
    return periodLabel;
  };

  const getProvisionedIncludedItemLabel = (
    feature?: BillingFeatureEnum,
    item?: BillingUpfrontProvisionedUsageItemFragment,
    period?: BillingPeriodEnum,
  ) => {
    if (
      !feature ||
      (!item?.fixedQuantity &&
        !item?.proportionalQuantity &&
        !item?.packageSize)
    ) {
      return '';
    }

    const featureLabel = getConcatenatedFeaturesLabel(
      item?.proportionalQuantity?.dependerFeature,
    );
    const perFeature = item?.proportionalQuantity
      ? ` ${t('common:per')} ${featureLabel}`
      : '';
    const quantity = getFeatureQuantitiesUnit(
      feature,
      Number(item?.fixedQuantity ?? 1) * Number(item?.packageSize ?? 1),
    );

    return `${quantity}${perFeature}${renderPeriodLabel(period)}`;
  };

  const getProvisionedConfigurableItemLabel = (
    feature?: BillingFeatureEnum,
    item?: BillingUpfrontProvisionedUsageItemFragment,
    period?: BillingPeriodEnum,
  ) => {
    if (!feature) {
      return '';
    }

    const unit = getFeatureUnit(feature);
    const formattedTime = formatTimeUnit({
      minutes: Number(item?.packageSize),
    });

    let quantityOfLabel = t('billing:quantity_of_unit', {
      unit: capitalizeString(getFeatureUnit(feature)),
    });

    if (unit.includes('minute') && formattedTime) {
      return `${t('billing:quantity_of_unit', {
        unit: t(`units:${formattedTime.key}`),
      })}${renderPeriodLabel(period)}`;
    }

    if (Number(item?.packageSize) > 0) {
      quantityOfLabel = t('billing:quantity_of_packages', {
        packageSize: getFeatureQuantitiesUnit(
          feature,
          Number(item?.packageSize),
        ),
      });
    }

    return `${quantityOfLabel}${renderPeriodLabel(period)}`;
  };

  return {
    getTierLabel,
    getVersionLabel,
    getFeatureLabel,
    getFeatureIcon,
    getFeatureUnit,
    getFeatureQuantitiesUnit,
    isFormattedFeatureUnit,
    getOperationLabel,
    getPeriodLabel,
    getDurationLabel,
    getProvisionedIncludedItemLabel,
    getProvisionedConfigurableItemLabel,
    getConcatenatedFeaturesLabel,
  };
};
